import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
  BarChart, Bar, LineChart, Line, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from "recharts";
import { ProtectedRoute } from "../components/ProtectedRoute";
import { CPCADashboard } from "./CPCADashboard";
import {
  fetchMorosos,
  MorosResponse,
  MorosoRecord,
  AgingBucket,
} from "../api/morosos";

// ─── Constants ────────────────────────────────────────────────────────────────

const AGING_LABELS: Record<AgingBucket, string> = {
  "current": "Al día",
  "30-60":  "30–60 días",
  "60-90":  "60–90 días",
  "90-180": "90–180 días",
  "180+":   "180+ días",
};

const AGING_COLOR: Record<string, string> = {
  "30-60":  "#f59e0b",
  "60-90":  "#f97316",
  "90-180": "#ef4444",
  "180+":   "#991b1b",
};

const PIE_COLORS = ["#f59e0b", "#f97316", "#ef4444", "#991b1b"];

// ─── Helpers ──────────────────────────────────────────────────────────────────

function fmtCurrency(n: number) {
  return n.toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
  });
}

function fmtDate(d: string | null) {
  if (!d) return "—";
  return new Date(d + "T00:00:00").toLocaleDateString("es-PR");
}

function agingBadgeClass(bucket: AgingBucket) {
  const map: Record<AgingBucket, string> = {
    "current": "bg-green-100 text-green-700",
    "30-60":  "bg-amber-100 text-amber-700",
    "60-90":  "bg-orange-100 text-orange-700",
    "90-180": "bg-red-100 text-red-700",
    "180+":   "bg-red-900 text-white",
  };
  return map[bucket] ?? "bg-gray-100 text-gray-600";
}

type SortField = "namecust" | "cuscod" | "amtowing" | "maxDaysOverdue" | "datelastpymt" | "amtlastpymt" | "invoiceCount";
type SortDir = "asc" | "desc";

// ─── SortableHeader ───────────────────────────────────────────────────────────

function SortableHeader({
  label, field, sortField, sortDir, onSort,
}: {
  label: string;
  field: SortField;
  sortField: SortField;
  sortDir: SortDir;
  onSort: (f: SortField) => void;
}) {
  const active = sortField === field;
  return (
    <th
      className="px-3 py-2.5 text-left text-xs font-semibold text-gray-500 uppercase cursor-pointer select-none hover:text-gray-700 whitespace-nowrap"
      onClick={() => onSort(field)}
    >
      {label}{" "}
      <span className={active ? "text-brand-800" : "text-gray-300"}>
        {active ? (sortDir === "asc" ? "▲" : "▼") : "▲▼"}
      </span>
    </th>
  );
}

// ─── StatCard ─────────────────────────────────────────────────────────────────

function StatCard({
  label, value, sub, accentColor,
}: {
  label: string;
  value: string;
  sub?: string;
  accentColor: string;
}) {
  return (
    <div className={`bg-white rounded-xl border border-gray-200 border-l-4 ${accentColor} p-5`}>
      <div className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">{label}</div>
      <div className="text-2xl font-bold text-gray-900">{value}</div>
      {sub && <div className="text-xs text-gray-400 mt-0.5">{sub}</div>}
    </div>
  );
}

// ─── Excel (CSV) export ───────────────────────────────────────────────────────

function exportToCSV(records: MorosoRecord[]) {
  const BOM = "﻿";
  const headers = [
    "Contrato (CUSCOD)",
    "Inquilino",
    "Balance Pendiente",
    "Monto Vencido",
    "Categoría Morosidad",
    "Días Vencido",
    "Facturas Pendientes",
    "Último Pago",
    "Monto Último Pago",
    "Factura Más Antigua",
  ];

  const escape = (v: string | number | null) => {
    if (v === null || v === undefined) return "";
    const s = String(v);
    return s.includes(",") || s.includes('"') || s.includes("\n")
      ? `"${s.replace(/"/g, '""')}"`
      : s;
  };

  const rows = records.map((r) => [
    escape(r.cuscod),
    escape(r.namecust),
    escape(r.amtowing.toFixed(2)),
    escape(r.amtdue.toFixed(2)),
    escape(AGING_LABELS[r.agingBucket]),
    escape(r.maxDaysOverdue),
    escape(r.invoiceCount),
    escape(fmtDate(r.datelastpymt)),
    escape(r.amtlastpymt.toFixed(2)),
    escape(fmtDate(r.earliestDueDate)),
  ].join(","));

  const csv = BOM + [headers.join(","), ...rows].join("\r\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const link = document.createElement("a");
  link.href = URL.createObjectURL(blob);
  link.download = `morosos_${new Date().toISOString().split("T")[0]}.csv`;
  link.click();
  URL.revokeObjectURL(link.href);
}

// ─── DashboardContent ─────────────────────────────────────────────────────────

const DashboardContent: React.FC = () => {
  const [data, setData] = useState<MorosResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);

  // Client-side filters
  const [agingFilter, setAgingFilter] = useState<AgingBucket | "all">("all");
  const [search, setSearch] = useState("");
  const [minBalance, setMinBalance] = useState(0);
  const [sortField, setSortField] = useState<SortField>("amtowing");
  const [sortDir, setSortDir] = useState<SortDir>("desc");

  const load = useCallback(async (refresh: boolean) => {
    if (refresh) setRefreshing(true);
    else { setLoading(true); setError(null); }
    try {
      const result = await fetchMorosos(refresh);
      setData(result);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : "Error desconocido");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => { load(false); }, [load]);

  const handleSort = useCallback((field: SortField) => {
    setSortDir((d) => (sortField === field && d === "desc" ? "asc" : "desc"));
    setSortField(field);
  }, [sortField]);

  // ── Derived / memoised ───────────────────────────────────────────────────

  const maxBalance = useMemo(
    () => (data ? Math.max(...data.records.map((r) => r.amtowing), 1) : 1),
    [data]
  );

  const filtered = useMemo(() => {
    if (!data) return [];
    const q = search.toLowerCase().trim();
    return data.records
      .filter((r) => agingFilter === "all" || r.agingBucket === agingFilter)
      .filter((r) => r.amtowing >= minBalance)
      .filter((r) =>
        !q || r.namecust.toLowerCase().includes(q) || r.cuscod.toLowerCase().includes(q)
      )
      .slice()
      .sort((a, b) => {
        const m = sortDir === "asc" ? 1 : -1;
        switch (sortField) {
          case "amtowing":      return (a.amtowing - b.amtowing) * m;
          case "maxDaysOverdue":return (a.maxDaysOverdue - b.maxDaysOverdue) * m;
          case "amtlastpymt":  return (a.amtlastpymt - b.amtlastpymt) * m;
          case "invoiceCount":  return (a.invoiceCount - b.invoiceCount) * m;
          case "namecust":      return a.namecust.localeCompare(b.namecust) * m;
          case "cuscod":        return a.cuscod.localeCompare(b.cuscod) * m;
          case "datelastpymt":
            return ((a.datelastpymt ?? "0") < (b.datelastpymt ?? "0") ? -1 : 1) * m;
          default: return 0;
        }
      });
  }, [data, agingFilter, search, minBalance, sortField, sortDir]);

  // Aging amount bar chart data
  const agingAmountData = useMemo(() => {
    if (!data) return [];
    const buckets: AgingBucket[] = ["30-60", "60-90", "90-180", "180+"];
    return buckets.map((b) => ({
      bucket: AGING_LABELS[b],
      amount: data.records
        .filter((r) => r.agingBucket === b)
        .reduce((s, r) => s + r.amtowing, 0),
      fill: AGING_COLOR[b],
    }));
  }, [data]);

  // Top-10 bar chart (reversed so highest is at top on horizontal layout)
  const top10Data = useMemo(() => {
    if (!data) return [];
    return [...data.records]
      .sort((a, b) => b.amtowing - a.amtowing)
      .slice(0, 10)
      .map((r) => ({
        name: r.namecust.length > 22 ? r.namecust.slice(0, 22) + "…" : r.namecust,
        amount: r.amtowing,
      }))
      .reverse();
  }, [data]);

  // Pie: count of tenants per aging bucket
  const pieData = useMemo(() => {
    if (!data) return [];
    const buckets: AgingBucket[] = ["30-60", "60-90", "90-180", "180+"];
    return buckets
      .map((b, i) => ({
        name: AGING_LABELS[b],
        value: data.records.filter((r) => r.agingBucket === b).length,
        fill: PIE_COLORS[i],
      }))
      .filter((d) => d.value > 0);
  }, [data]);

  // ── Render states ────────────────────────────────────────────────────────

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 text-center">
        <div className="inline-block w-8 h-8 border-4 border-brand-800 border-t-transparent rounded-full animate-spin mb-4" />
        <p className="text-gray-500">Consultando Sage 300 (Accpac)…</p>
      </div>
    );
  }

  if (error && !data) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 text-center">
        <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-red-100 mb-4">
          <span className="text-2xl">⚠️</span>
        </div>
        <h3 className="text-lg font-semibold text-gray-900 mb-2">Sage 300 no disponible</h3>
        <p className="text-sm text-gray-500 mb-6 max-w-md mx-auto">{error}</p>
        <button
          onClick={() => load(false)}
          className="px-4 py-2 bg-brand-800 text-white text-sm font-medium rounded-lg hover:bg-brand-700 transition"
        >
          Reintentar
        </button>
      </div>
    );
  }

  if (!data) return null;

  const { summary, trend } = data;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-8">

      {/* Stale-cache warning */}
      {data.warning && (
        <div className="flex items-start gap-3 px-4 py-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800">
          <span className="text-lg leading-none mt-0.5">⚠</span>
          <span>{data.warning}</span>
        </div>
      )}

      {/* Header */}
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Dashboard de Morosos</h1>
          <p className="text-gray-500 mt-1 text-sm">
            Arrendatarios con balance pendiente en Sage 300 (ATPR)
          </p>
          <p className="text-xs text-gray-400 mt-1">
            Última actualización:{" "}
            {new Date(data.lastUpdated).toLocaleString("es-PR")}{" "}
            {data.source === "cache" && (
              <span className="ml-1 px-1.5 py-0.5 rounded text-amber-700 bg-amber-100 font-medium">
                caché
              </span>
            )}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => load(true)}
            disabled={refreshing}
            className="px-3 py-2 bg-white border border-gray-200 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-50 transition disabled:opacity-50 flex items-center gap-1.5"
          >
            <span className={refreshing ? "animate-spin inline-block" : ""}>↺</span>
            {refreshing ? "Actualizando…" : "Actualizar"}
          </button>
          <button
            onClick={() => exportToCSV(filtered)}
            className="px-3 py-2 bg-brand-800 text-white text-sm font-medium rounded-lg hover:bg-brand-700 transition"
          >
            Exportar Excel
          </button>
        </div>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
        <StatCard
          label="Clientes morosos"
          value={String(summary.totalTenants)}
          sub="con balance > $0"
          accentColor="border-red-500"
        />
        <StatCard
          label="Total pendiente"
          value={fmtCurrency(summary.totalOwing)}
          accentColor="border-red-700"
        />
        <StatCard
          label="30–60 días"
          value={String(summary.bucket30_60)}
          sub="arrendatarios"
          accentColor="border-amber-400"
        />
        <StatCard
          label="60–90 días"
          value={String(summary.bucket60_90)}
          sub="arrendatarios"
          accentColor="border-orange-400"
        />
        <StatCard
          label="90+ días"
          value={String(summary.bucket90_180 + summary.bucket180plus)}
          sub="arrendatarios"
          accentColor="border-red-500"
        />
        <StatCard
          label="Sin pago 90+ días"
          value={String(summary.noPayment90plus)}
          sub="arrendatarios"
          accentColor="border-gray-400"
        />
      </div>

      {/* Charts row 1: Aging amount + Top 10 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">

        {/* Bar: amount by aging bucket */}
        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <h2 className="text-sm font-semibold text-gray-700 mb-4">Balance por categoría</h2>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={agingAmountData} margin={{ top: 4, right: 16, bottom: 0, left: 16 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
              <XAxis dataKey="bucket" tick={{ fontSize: 11 }} />
              <YAxis
                tick={{ fontSize: 11 }}
                tickFormatter={(v: number) =>
                  v >= 1_000_000
                    ? `$${(v / 1_000_000).toFixed(1)}M`
                    : v >= 1_000
                    ? `$${(v / 1_000).toFixed(0)}K`
                    : `$${v}`
                }
              />
              <Tooltip
                formatter={(v) => [v != null ? fmtCurrency(Number(v)) : "—", "Balance"]}
                contentStyle={{ fontSize: 12 }}
              />
              <Bar dataKey="amount" radius={[4, 4, 0, 0]}>
                {agingAmountData.map((entry, i) => (
                  <Cell key={i} fill={entry.fill} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Bar: Top 10 morosos (horizontal) */}
        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <h2 className="text-sm font-semibold text-gray-700 mb-4">Top 10 morosos por balance</h2>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart
              data={top10Data}
              layout="vertical"
              margin={{ top: 4, right: 16, bottom: 0, left: 8 }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" horizontal={false} />
              <XAxis
                type="number"
                tick={{ fontSize: 10 }}
                tickFormatter={(v: number) =>
                  v >= 1_000 ? `$${(v / 1_000).toFixed(0)}K` : `$${v}`
                }
              />
              <YAxis type="category" dataKey="name" width={130} tick={{ fontSize: 10 }} />
              <Tooltip
                formatter={(v) => [v != null ? fmtCurrency(Number(v)) : "—", "Balance"]}
                contentStyle={{ fontSize: 12 }}
              />
              <Bar dataKey="amount" fill="#ef4444" radius={[0, 4, 4, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Charts row 2: Pie + 12-month trend */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">

        {/* Pie: count distribution */}
        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <h2 className="text-sm font-semibold text-gray-700 mb-4">Distribución por categoría (arrendatarios)</h2>
          {pieData.length === 0 ? (
            <div className="h-48 flex items-center justify-center text-sm text-gray-400">
              Sin datos de morosidad
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={220}>
              <PieChart>
                <Pie
                  data={pieData}
                  cx="50%"
                  cy="50%"
                  outerRadius={80}
                  dataKey="value"
                  label={({ name, percent }) =>
                    `${name} (${((percent ?? 0) * 100).toFixed(0)}%)`
                  }
                  labelLine={false}
                >
                  {pieData.map((entry, i) => (
                    <Cell key={i} fill={entry.fill} />
                  ))}
                </Pie>
                <Tooltip
                  formatter={(v) => [`${v ?? 0} arrendatarios`, "Cantidad"]}
                  contentStyle={{ fontSize: 12 }}
                />
                <Legend iconSize={10} wrapperStyle={{ fontSize: 11 }} />
              </PieChart>
            </ResponsiveContainer>
          )}
        </div>

        {/* Line: 12-month billing trend */}
        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <h2 className="text-sm font-semibold text-gray-700 mb-1">
            Actividad de facturación — últimos 12 meses
          </h2>
          <p className="text-xs text-gray-400 mb-3">
            Total facturado por mes en ARINV (Sage 300)
          </p>
          {trend.length === 0 ? (
            <div className="h-48 flex items-center justify-center text-sm text-gray-400">
              Sin datos de tendencia
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={200}>
              <LineChart data={trend} margin={{ top: 4, right: 16, bottom: 0, left: 16 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
                <XAxis
                  dataKey="month"
                  tick={{ fontSize: 10 }}
                  tickFormatter={(v: string) => {
                    const [y, m] = v.split("-");
                    return `${m}/${y?.slice(2)}`;
                  }}
                />
                <YAxis
                  tick={{ fontSize: 10 }}
                  tickFormatter={(v: number) =>
                    v >= 1_000 ? `$${(v / 1_000).toFixed(0)}K` : `$${v}`
                  }
                />
                <Tooltip
                  formatter={(v) => [v != null ? fmtCurrency(Number(v)) : "—", "Facturado"]}
                  contentStyle={{ fontSize: 12 }}
                />
                <Line
                  type="monotone"
                  dataKey="totalInvoiced"
                  stroke="#4f46e5"
                  strokeWidth={2}
                  dot={{ r: 3 }}
                  activeDot={{ r: 5 }}
                />
              </LineChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl border border-gray-200 p-4">
        <div className="flex flex-wrap items-center gap-3">
          {/* Search */}
          <input
            type="text"
            placeholder="Buscar por nombre o contrato…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="flex-1 min-w-48 px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm text-gray-700 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-brand-800"
          />

          {/* Aging bucket filter */}
          <select
            value={agingFilter}
            onChange={(e) => setAgingFilter(e.target.value as AgingBucket | "all")}
            className="px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-brand-800"
          >
            <option value="all">Todas las categorías</option>
            {(["30-60", "60-90", "90-180", "180+"] as AgingBucket[]).map((b) => (
              <option key={b} value={b}>{AGING_LABELS[b]}</option>
            ))}
          </select>

          {/* Minimum balance */}
          <div className="flex items-center gap-2">
            <label className="text-xs font-medium text-gray-500 whitespace-nowrap">
              Balance mín: {fmtCurrency(minBalance)}
            </label>
            <input
              type="range"
              min={0}
              max={Math.ceil(maxBalance / 1000) * 1000}
              step={100}
              value={minBalance}
              onChange={(e) => setMinBalance(Number(e.target.value))}
              className="w-32 accent-brand-800"
            />
          </div>

          <span className="text-xs text-gray-400 ml-auto">
            {filtered.length} de {data.records.length} arrendatarios
          </span>
        </div>
      </div>

      {/* Table */}
      {filtered.length === 0 ? (
        <div className="bg-white rounded-xl border border-gray-200 p-12 text-center text-gray-400">
          No hay arrendatarios con los filtros seleccionados.
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-x-auto">
          <table className="w-full min-w-[900px]">
            <thead className="bg-gray-50 border-b border-gray-100">
              <tr>
                <th className="px-3 py-2.5 text-left text-xs font-semibold text-gray-500 uppercase w-10">#</th>
                <SortableHeader label="Contrato" field="cuscod" sortField={sortField} sortDir={sortDir} onSort={handleSort} />
                <SortableHeader label="Inquilino" field="namecust" sortField={sortField} sortDir={sortDir} onSort={handleSort} />
                <SortableHeader label="Balance" field="amtowing" sortField={sortField} sortDir={sortDir} onSort={handleSort} />
                <th className="px-3 py-2.5 text-left text-xs font-semibold text-gray-500 uppercase">Proporción</th>
                <SortableHeader label="Días" field="maxDaysOverdue" sortField={sortField} sortDir={sortDir} onSort={handleSort} />
                <th className="px-3 py-2.5 text-left text-xs font-semibold text-gray-500 uppercase">Categoría</th>
                <SortableHeader label="Último pago" field="datelastpymt" sortField={sortField} sortDir={sortDir} onSort={handleSort} />
                <SortableHeader label="Monto pago" field="amtlastpymt" sortField={sortField} sortDir={sortDir} onSort={handleSort} />
                <SortableHeader label="Facturas" field="invoiceCount" sortField={sortField} sortDir={sortDir} onSort={handleSort} />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {filtered.map((r, i) => (
                <tr key={r.cuscod} className="hover:bg-gray-50 transition">
                  <td className="px-3 py-3">
                    <span className={`inline-flex items-center justify-center w-6 h-6 rounded-full text-xs font-bold ${
                      i === 0 ? "bg-red-600 text-white"
                      : i === 1 ? "bg-orange-500 text-white"
                      : i === 2 ? "bg-amber-500 text-white"
                      : "bg-gray-100 text-gray-500"
                    }`}>
                      {i + 1}
                    </span>
                  </td>
                  <td className="px-3 py-3">
                    <span className="font-mono text-xs text-gray-600 bg-gray-100 px-1.5 py-0.5 rounded">
                      {r.cuscod}
                    </span>
                  </td>
                  <td className="px-3 py-3">
                    <div className="font-medium text-gray-900 text-sm">{r.namecust}</div>
                    {r.earliestDueDate && (
                      <div className="text-xs text-gray-400 mt-0.5">
                        Fact. más antigua: {fmtDate(r.earliestDueDate)}
                      </div>
                    )}
                  </td>
                  <td className="px-3 py-3 text-right">
                    <span className="text-sm font-bold text-red-600">{fmtCurrency(r.amtowing)}</span>
                    {r.amtdue > 0 && r.amtdue !== r.amtowing && (
                      <div className="text-xs text-gray-400">vencido: {fmtCurrency(r.amtdue)}</div>
                    )}
                  </td>
                  <td className="px-3 py-3">
                    <div className="w-24 h-2 bg-gray-100 rounded-full overflow-hidden">
                      <div
                        className="h-full rounded-full bg-gradient-to-r from-red-400 to-red-600"
                        style={{ width: `${(r.amtowing / maxBalance) * 100}%` }}
                      />
                    </div>
                  </td>
                  <td className="px-3 py-3 text-center">
                    <span className={`text-sm font-semibold ${
                      r.maxDaysOverdue > 180 ? "text-red-700"
                      : r.maxDaysOverdue > 90 ? "text-red-500"
                      : r.maxDaysOverdue > 60 ? "text-orange-500"
                      : "text-amber-600"
                    }`}>
                      {r.maxDaysOverdue > 0 ? `${r.maxDaysOverdue}d` : "—"}
                    </span>
                  </td>
                  <td className="px-3 py-3">
                    <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-semibold ${agingBadgeClass(r.agingBucket)}`}>
                      {AGING_LABELS[r.agingBucket]}
                    </span>
                  </td>
                  <td className="px-3 py-3 text-sm text-gray-600">{fmtDate(r.datelastpymt)}</td>
                  <td className="px-3 py-3 text-sm text-gray-600 text-right">
                    {r.amtlastpymt > 0 ? fmtCurrency(r.amtlastpymt) : "—"}
                  </td>
                  <td className="px-3 py-3 text-center">
                    <span className="text-sm text-gray-500">{r.invoiceCount || "—"}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <p className="text-xs text-gray-400">
        * Datos en tiempo real de Sage 300 (ATPRDAT). Caché de 15 minutos.
        Balance pendiente y días vencido desde ARCUS + ARINV.
        Municipio disponible vía cruce con Dataverse AT-Contratos (extensión futura).
      </p>
    </div>
  );
};

// ─── DashboardPage (tab container — unchanged) ────────────────────────────────

type DashTab = "morosos" | "cpca";

export const DashboardPage: React.FC = () => {
  const [tab, setTab] = useState<DashTab>("morosos");

  return (
    <ProtectedRoute>
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex gap-1 pt-2">
            {(
              [
                { key: "morosos", label: "Morosos" },
                { key: "cpca",    label: "CPCA — Comité" },
              ] as { key: DashTab; label: string }[]
            ).map(({ key, label }) => (
              <button
                key={key}
                onClick={() => setTab(key)}
                className={`px-4 py-2.5 text-sm font-medium rounded-t-lg border-b-2 transition ${
                  tab === key
                    ? "border-brand-800 text-brand-800 bg-brand-50"
                    : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                }`}
              >
                {label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {tab === "morosos" && <DashboardContent />}
      {tab === "cpca"    && <CPCADashboard />}
    </ProtectedRoute>
  );
};
